(() => {
  if (window.AromaticaCartOffers) return;

  const configSelector = '[data-ap-cart-offers-config]';
  const rootSelector = '[data-ap-cart-offers-root]';
  const supportedTypes = new Set(['buy_x_get_y', 'quantity', 'spend']);
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatTemplate = (template, values) => Object.entries(values).reduce(
    (output, [key, value]) => output.replaceAll(`{${key}}`, value).replaceAll(`__${key}__`, value),
    String(template ?? '')
  );

  const normalizedTitle = (value) => String(value ?? '').trim().toLocaleLowerCase();

  const numericIds = (value) => String(value ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter((id) => /^\d+$/.test(id));

  const discountEvidence = (cart) => {
    const titles = [];
    for (const application of cart.cart_level_discount_applications ?? []) {
      if (application?.title) titles.push(application.title);
    }
    for (const item of cart.items ?? []) {
      for (const discount of item.discounts ?? []) {
        if (discount?.title) titles.push(discount.title);
      }
    }
    return new Set(titles.map(normalizedTitle).filter(Boolean));
  };

  const eligibleItems = (offer, cart) => {
    let items = [...(cart.items ?? [])];
    if (offer.excludeGiftCards) items = items.filter((item) => !item.gift_card);

    if (offer.scope === 'all_products') return items;
    if (offer.scope === 'selected_products') {
      const ids = new Set([
        ...(offer.productIds ?? []).map(String),
        ...numericIds(offer.productIdText)
      ]);
      if (!ids.size) return null;
      return items.filter((item) => ids.has(String(item.product_id)));
    }
    if (offer.scope === 'selected_variants') {
      const ids = new Set(numericIds(offer.variantIds));
      if (!ids.size) return null;
      return items.filter((item) => ids.has(String(item.variant_id)));
    }
    return null;
  };

  const isActiveDate = (offer, currentDate) => {
    if (!datePattern.test(currentDate ?? '')) return false;
    if (offer.startDate && !datePattern.test(offer.startDate)) return false;
    if (offer.endDate && !datePattern.test(offer.endDate)) return false;
    if (offer.startDate && offer.endDate && offer.startDate > offer.endDate) return false;
    if (offer.startDate && currentDate < offer.startDate) return false;
    if (offer.endDate && currentDate > offer.endDate) return false;
    return true;
  };

  const evaluateOffers = (config, cart) => {
    const confirmedDiscounts = discountEvidence(cart);
    const results = [];

    for (const offer of config.offers ?? []) {
      if (!offer.enabled || !supportedTypes.has(offer.type) || !offer.title || !isActiveDate(offer, config.currentDate)) continue;
      const items = eligibleItems(offer, cart);
      if (!items) continue;

      let current = 0;
      let target = 0;
      if (offer.type === 'spend') {
        target = Number(offer.spendThresholdCents);
        current = items.reduce((sum, item) => sum + Number(item.final_line_price ?? 0), 0);
      } else {
        target = Number(offer.quantityThreshold);
        current = items.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0);
      }
      if (!Number.isFinite(target) || target <= 0) continue;

      const ratio = Math.min(Math.max(current / target, 0), 1);
      const discountTitle = normalizedTitle(offer.discountTitle);
      const applied = Boolean(discountTitle && confirmedDiscounts.has(discountTitle));
      const qualified = current >= target;
      const status = applied ? 'applied' : qualified ? 'qualified' : 'progress';

      results.push({
        ...offer,
        applied,
        current,
        qualified,
        ratio,
        remaining: Math.max(target - current, 0),
        status,
        target
      });
    }

    const rank = { applied: 3, qualified: 2, progress: 1 };
    return results.sort((a, b) => (
      b.ratio - a.ratio
      || rank[b.status] - rank[a.status]
      || Number(b.priority ?? 0) - Number(a.priority ?? 0)
    ));
  };

  const formatMoney = (cents, cart, config) => {
    const currency = cart.currency || window.Shopify?.currency?.active || 'CAD';
    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(Number(cents) / 100);
    } catch {
      return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
    }
  };

  const familyLabel = (offer, strings) => {
    if (offer.type === 'buy_x_get_y') return strings.buyXGetY;
    if (offer.type === 'spend') return strings.spendReward;
    return strings.quantityDeal;
  };

  const statusText = (offer, cart, config) => {
    const strings = config.strings;
    if (offer.applied) return strings.applied;
    if (offer.qualified) return strings.qualified;
    if (offer.type === 'spend') {
      return formatTemplate(strings.spendRemaining, {
        amount: formatMoney(offer.remaining, cart, config)
      });
    }
    const key = offer.remaining === 1 ? 'itemRemaining' : 'itemsRemaining';
    return formatTemplate(strings[key], { count: String(offer.remaining) });
  };

  const compactSummary = (offer, cart, config) => {
    if (offer.applied) return offer.appliedSummary || config.strings.applied;
    if (offer.qualified) return config.strings.qualified;
    if (offer.type === 'spend') return statusText(offer, cart, config);
    const template = offer.remaining === 1 ? offer.progressSingular : offer.progressPlural;
    return template
      ? formatTemplate(template, { count: String(offer.remaining) })
      : statusText(offer, cart, config);
  };

  const localizedActionUrl = (value) => {
    const url = String(value ?? '').trim();
    if (!url.startsWith('/')) return url;
    const localeRoot = window.Shopify?.routes?.root || '/';
    if (localeRoot === '/' || url.startsWith(localeRoot)) return url;
    return `${localeRoot.replace(/\/$/, '')}${url}`;
  };

  const offerCard = (offer, cart, config, primary, disclosures) => {
    const summary = compactSummary(offer, cart, config);
    const key = `offer:${offer.id}`;
    const isOpen = disclosures.get(key) === true;
    const date = offer.endDate
      ? `<span class="ap-cart-offer__date">${escapeHtml(formatTemplate(config.strings.ends, { date: offer.endDate }))}</span>`
      : '';
    const reward = offer.reward ? `<p class="ap-cart-offer__reward">${escapeHtml(offer.reward)}</p>` : '';
    const verification = offer.applied
      ? config.strings.appliedDetail
      : offer.qualified
        ? config.strings.qualifiedDetail
        : '';
    const verificationMarkup = verification
      ? `<p class="ap-cart-offer__verification">${escapeHtml(verification)}</p>`
      : '';
    const terms = offer.terms ? `<p class="ap-cart-offer__terms">${escapeHtml(offer.terms)}</p>` : '';
    const disclaimer = primary && config.disclaimer
      ? `<p class="ap-cart-offers__disclaimer">${escapeHtml(config.disclaimer)}</p>`
      : '';
    const actionUrl = localizedActionUrl(offer.actionUrl);
    const action = offer.qualified && !offer.applied && offer.qualifiedAction
      ? actionUrl
        ? `<a class="ap-cart-offer__action" data-ap-offer-action="${escapeHtml(offer.id)}" href="${escapeHtml(actionUrl)}">${escapeHtml(offer.qualifiedAction)}</a>`
        : `<span class="ap-cart-offer__action ap-cart-offer__action--text">${escapeHtml(offer.qualifiedAction)}</span>`
      : '';
    const percent = Math.round(offer.ratio * 100);
    const progress = offer.applied
      ? ''
      : `<div class="ap-cart-offer__track" role="progressbar" aria-label="${escapeHtml(offer.title)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}">
          <span class="ap-cart-offer__bar" style="width:${percent}%"></span>
        </div>`;

    return `
      <article class="ap-cart-offer${primary ? ' ap-cart-offer--primary' : ''}${offer.applied ? ' ap-cart-offer--applied' : ''}">
        <div class="ap-cart-offer__statusline">
          <h3 class="ap-cart-offer__title">${escapeHtml(offer.title)}</h3>
          <span class="ap-cart-offer__status">${escapeHtml(summary)}</span>
        </div>
        ${progress}
        ${action}
        <details class="ap-cart-offer__details" data-ap-disclosure-key="${escapeHtml(key)}"${isOpen ? ' open' : ''}>
          <summary>
            <span class="ap-cart-offer__details-show">${escapeHtml(config.strings.offerDetails)}</span>
            <span class="ap-cart-offer__details-hide">${escapeHtml(config.strings.hideDetails)}</span>
            <span class="ap-cart-offer__details-icon" aria-hidden="true"></span>
          </summary>
          <div class="ap-cart-offer__details-content">
            <div class="ap-cart-offer__topline">
              <p class="ap-cart-offer__family">${escapeHtml(familyLabel(offer, config.strings))}</p>
              ${date}
            </div>
            ${reward}
            ${verificationMarkup}
            ${terms}
            ${disclaimer}
          </div>
        </details>
      </article>`;
  };

  const renderMarkup = (offers, cart, config, disclosures = new Map()) => {
    const primary = offerCard(offers[0], cart, config, true, disclosures);
    const secondary = offers.slice(1);
    const secondaryOpen = disclosures.get('secondary') === true;
    const secondaryMarkup = secondary.length
      ? `<details class="ap-cart-offers__secondary" data-ap-disclosure-key="secondary"${secondaryOpen ? ' open' : ''}>
          <summary>${escapeHtml(formatTemplate(config.strings.moreOffers, { count: String(secondary.length) }))}</summary>
          <div class="ap-cart-offers__secondary-list">${secondary.map((offer) => offerCard(offer, cart, config, false, disclosures)).join('')}</div>
        </details>`
      : '';

    return `<section class="ap-cart-offers__shell" aria-label="${escapeHtml(config.heading)}">
      <h2 class="ap-cart-offers__heading">${escapeHtml(config.heading)}</h2>
      ${primary}
      ${secondaryMarkup}
    </section>`;
  };

  const readConfig = () => {
    const element = document.querySelector(configSelector);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch {
      return null;
    }
  };

  class CartOffersController {
    constructor() {
      this.sequence = 0;
      this.timer = 0;
      this.lastAnnouncement = new WeakMap();
      this.disclosureState = new WeakMap();
      this.observe = this.observe.bind(this);
      this.schedule = this.schedule.bind(this);
    }

    start() {
      this.refresh(false);
      document.addEventListener('eurus:cart:items-changed', this.schedule);
      document.addEventListener('eurus:cart:discount-code:change', this.schedule);
      document.addEventListener('eurus:product:added', this.schedule);
      document.addEventListener('shopify:section:load', this.schedule);
      this.observer = new MutationObserver(this.observe);
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    observe(mutations) {
      const changed = mutations.some((mutation) => [...mutation.addedNodes].some((node) => (
        node.nodeType === 1
        && (node.matches?.(rootSelector) || node.querySelector?.(rootSelector) || node.matches?.(configSelector) || node.querySelector?.(configSelector))
      )));
      if (changed) this.schedule();
    }

    schedule() {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.refresh(true), 120);
    }

    hideAll() {
      document.querySelectorAll(rootSelector).forEach((root) => {
        root.hidden = true;
        const content = root.querySelector('[data-ap-cart-offers-content]');
        if (content) content.replaceChildren();
      });
    }

    async refresh(announce = true) {
      const config = readConfig();
      if (!config || !(config.offers ?? []).length) {
        this.hideAll();
        return;
      }

      const sequence = ++this.sequence;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        const cart = await response.json();
        if (sequence !== this.sequence) return;
        this.render(cart, config, announce);
      } catch {
        this.hideAll();
      }
    }

    render(cart, config, announce) {
      const offers = evaluateOffers(config, cart);
      const roots = document.querySelectorAll(rootSelector);
      if (!offers.length || Number(cart.item_count ?? 0) === 0) {
        this.hideAll();
        return;
      }

      const announcement = formatTemplate(config.strings.updated, {
        title: offers[0].title,
        status: compactSummary(offers[0], cart, config)
      });

      roots.forEach((root) => {
        const content = root.querySelector('[data-ap-cart-offers-content]');
        const live = root.querySelector('[data-ap-cart-offers-live]');
        if (!content || !live) return;
        let disclosures = this.disclosureState.get(root);
        if (!disclosures) {
          disclosures = new Map();
          this.disclosureState.set(root, disclosures);
        }
        root.querySelectorAll('[data-ap-disclosure-key]').forEach((details) => {
          disclosures.set(details.dataset.apDisclosureKey, details.open);
        });
        const activeElement = document.activeElement;
        let focusTarget = null;
        if (activeElement && root.contains(activeElement)) {
          const action = activeElement.closest?.('[data-ap-offer-action]');
          const details = activeElement.closest?.('[data-ap-disclosure-key]');
          if (action) focusTarget = { type: 'action', key: action.dataset.apOfferAction };
          if (details && activeElement.matches('summary')) {
            focusTarget = { type: 'summary', key: details.dataset.apDisclosureKey };
          }
        }
        content.innerHTML = renderMarkup(offers, cart, config, disclosures);
        root.querySelectorAll('[data-ap-disclosure-key]').forEach((details) => {
          details.addEventListener('toggle', () => {
            disclosures.set(details.dataset.apDisclosureKey, details.open);
          });
        });
        if (focusTarget?.type === 'summary') {
          const summary = [...root.querySelectorAll('[data-ap-disclosure-key]')]
            .find((details) => details.dataset.apDisclosureKey === focusTarget.key)
            ?.querySelector(':scope > summary');
          summary?.focus({ preventScroll: true });
        }
        if (focusTarget?.type === 'action') {
          [...root.querySelectorAll('[data-ap-offer-action]')]
            .find((action) => action.dataset.apOfferAction === focusTarget.key)
            ?.focus({ preventScroll: true });
        }
        root.hidden = false;
        if (announce && root.getClientRects().length && this.lastAnnouncement.get(root) !== announcement) {
          live.textContent = announcement;
          this.lastAnnouncement.set(root, announcement);
        }
      });
    }
  }

  const api = { compactSummary, evaluateOffers, renderMarkup, refresh: () => undefined };
  window.AromaticaCartOffers = api;

  const init = () => {
    const controller = new CartOffersController();
    api.refresh = (announce = true) => controller.refresh(announce);
    controller.start();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
